import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";
import type { NotarialRequestEventDto } from "@/services/notaryRequestsService";
import type { NotarialRequestStatus } from "@/services/notaryRequestsService";

/**
 * Statuses the requester is not shown verbatim.
 *
 * `document_ready_at_professional` USED TO BE HERE and is deliberately gone
 * (spec f462e7d6). It was hidden because it leaked the platform handoff — the
 * consumer would have read "listo para retirar" about a pickup they were not
 * party to. Now it means the opposite: their own professional has finished the
 * document and is about to bring it to their door, which is the single most
 * useful thing the consumer can be told, and they are emailed and pushed about
 * it anyway (notarialGestionReady).
 *
 * `document_at_platform` stays, for the one in-flight row that could still be
 * sitting in it. Nothing writes it any more.
 */
const STATUSES_SHOWN_AS_IN_PROGRESS_TO_CONSUMER: NotarialRequestStatus[] = [
  "document_at_platform",
];

/**
 * Timeline events not shown to the requester.
 *
 * `physical_document_ready` LEFT THIS SET (spec f462e7d6). It used to be the
 * professional telling Gestionalo to come and collect, which was none of the
 * consumer's business; it is now the professional telling the CONSUMER the
 * document is finished, which is the whole point of the step.
 *
 * `physical_pickup_details_updated` and `platform_received_document` stay
 * hidden and are no longer emitted at all — they are kept here so a legacy
 * row on an old request does not surface a pickup the consumer never knew of.
 */
const CONSUMER_HIDDEN_EVENT_TYPES = new Set<string>([
  "physical_pickup_details_updated",
  "platform_received_document",
  "certificate_uploaded",
]);

export function getConsumerFacingNotarialStatus(status: NotarialRequestStatus): NotarialRequestStatus {
  return STATUSES_SHOWN_AS_IN_PROGRESS_TO_CONSUMER.includes(status) ? "in_progress" : status;
}

/** Same keys as consumer list/detail badges; internal handoff statuses use plain-language labels. */
const STATUS_LABEL_FOR_DISPLAY: Record<NotarialRequestStatus, string> = {
  // Post-payment "complete profile" gate. Surfaced as "En pausa" on the
  // consumer side so the user understands the request isn't lost — it's
  // just waiting on them to finish their profile.
  pending_profile_completion: "notary.requests.statusPendingProfileCompletion",
  // "Verificando pago" — the consumer has transferred and we are checking it.
  pending_payment_verification: "requests.user.statusPendingPaymentVerification",
  pending: "notary.requests.statusPending",
  in_progress: "notary.requests.statusInProgress",
  completed: "notary.requests.statusCompleted",
  missing_information: "notary.requests.statusMissingInfo",
  cannot_complete: "notary.requests.statusCannotComplete",
  document_ready_at_professional: "notary.requests.statusDocumentReadyAtProfessional",
  document_at_platform: "notary.requests.statusDocumentAtPlatform",
  awaiting_consumer_delivery_details: "notary.requests.statusAwaitingConsumerDelivery",
  awaiting_shipment_to_consumer: "notary.requests.statusAwaitingShipment",
  in_transit_to_consumer: "notary.requests.statusInTransitToConsumer",
};

const VARIANT_FOR_DISPLAY: Record<NotarialRequestStatus, StatusBadgeVariant> = {
  pending_profile_completion: "warning",
  pending_payment_verification: "warning",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  missing_information: "danger",
  cannot_complete: "softDanger",
  document_ready_at_professional: "warning",
  document_at_platform: "info",
  awaiting_consumer_delivery_details: "info",
  awaiting_shipment_to_consumer: "info",
  in_transit_to_consumer: "info",
};

/** i18n key for the status badge (consumer-facing). */
export function getConsumerStatusTranslationKey(status: NotarialRequestStatus): string {
  if (status === "document_ready_at_professional") {
    return "requests.user.statusConsumerDocumentReadyPreparingShipment";
  }
  if (status === "document_at_platform") {
    return "requests.user.statusConsumerShipmentProcessing";
  }
  return STATUS_LABEL_FOR_DISPLAY[getConsumerFacingNotarialStatus(status)];
}

/** Badge color for the consumer status chip. */
export function getConsumerStatusBadgeVariant(status: NotarialRequestStatus): StatusBadgeVariant {
  if (status === "document_ready_at_professional") {
    return "success";
  }
  if (status === "document_at_platform") {
    return "info";
  }
  return VARIANT_FOR_DISPLAY[getConsumerFacingNotarialStatus(status)] ?? "info";
}

/** Optional short hint under request details; `null` when none. */
export function getConsumerFulfillmentHintTranslationKey(
  status: NotarialRequestStatus,
): string | null {
  // The two steps the consumer can now see explain themselves, because the
  // status word alone ("Lista") does not say who brings it or when.
  if (status === "document_ready_at_professional") {
    return "requests.user.consumerFulfillmentHintDocumentReady";
  }
  if (status === "in_transit_to_consumer") {
    return "requests.user.consumerFulfillmentHintInTransit";
  }
  if (status === "document_at_platform") {
    return "requests.user.consumerFulfillmentHintShipmentProcessing";
  }
  return null;
}

/**
 * When true, hide the assigned notary/accountant card on the consumer detail
 * page.
 *
 * INVERTED FOR THE TWO LIVE DELIVERY STATUSES (spec f462e7d6). Hiding the
 * professional made sense while Gestionalo stood between them: once the parcel
 * was ours, the professional was done and naming them invited contact about a
 * shipment they had no part in. The professional is now the person who will
 * knock on the door, so concealing who they are is exactly backwards.
 *
 * The three retired statuses stay listed, unreachable, for any legacy row.
 */
export function shouldHideAssignedProfessionalFromConsumer(status: NotarialRequestStatus): boolean {
  return (
    status === "document_at_platform" ||
    status === "awaiting_consumer_delivery_details" ||
    status === "awaiting_shipment_to_consumer"
  );
}

export function filterConsumerVisibleActivityEvents(
  events: NotarialRequestEventDto[],
): NotarialRequestEventDto[] {
  return events.filter((e) => !CONSUMER_HIDDEN_EVENT_TYPES.has(e.eventType));
}
